import { type AccountSummary, type ClockCommand, REPLAY_SPEEDS, type ReplaySpeed } from "@trader/shared/Money"
import { useEffect, useState } from "react"
import { toast } from "../../ui/toasts.ts"
import { shownInstruments } from "../workspace/workspaceModel.ts"
import { useWorkspace } from "../workspace/workspaceStore.ts"
import { formatClock } from "./format.ts"
import { messageFor, tradingApi } from "./tradingApi.ts"
import { syncChartPreference, useFills, useOpenOrders, usePositions, useRunners, useSyncChart } from "./tradingData.ts"

const speedLabel = (speed: ReplaySpeed): string => (speed === "max" ? "Max" : `${speed}×`)

/**
 * Bar replay for a Replay account: play at 0.5, 1, 5 or 20 bars a second (or as fast
 * as possible), pause, step one bar, and the account's virtual date and time.
 */
export function ReplayControls({ account, compact = false }: {
  readonly account: AccountSummary
  /** The toolbar's copy (docs/design/0007): no status text, no Sync checkbox (its ✕ turns sync off). */
  readonly compact?: boolean
}) {
  const clock = account.clock
  const [busy, setBusy] = useState<ClockCommand["_tag"] | null>(null)
  const [speed, setSpeed] = useState<ReplaySpeed>(clock._tag === "Replay" ? clock.speed : 1)
  const sync = useSyncChart()
  // The replay steps through the open charts' bars as well as what the account trades
  // (docs/design/0011), so every chart moves; with neither, Play has nothing to replay.
  const follow = shownInstruments(useWorkspace())
  const follows = follow.length > 0 || usePositions().length + useOpenOrders().length + useFills().length + useRunners().length > 0
  // Another tab (or a reset) may change the speed; follow it.
  const serverSpeed = clock._tag === "Replay" ? clock.speed : 1
  useEffect(() => setSpeed(serverSpeed), [serverSpeed])
  if (clock._tag !== "Replay") return null

  const playing = clock.status === "Playing"
  const finished = clock.status === "Finished"
  const archived = account.status === "Archived"

  const send = (command: ClockCommand) => {
    setBusy(command._tag)
    tradingApi.clock(account.id, command).then(
      () => setBusy(null),
      (error: unknown) => {
        setBusy(null)
        const message = messageFor(error)
        toast("error", message.title, message.detail)
      }
    )
  }

  return (
    <div className="replay-controls" aria-label="Replay controls">
      {playing
        ? (
          <button type="button" className="replay-button" title="Pause" aria-label="Pause" disabled={archived} onClick={() => send({ _tag: "Pause" })}>
            <span aria-hidden>❚❚</span> Pause
          </button>
        )
        : (
          <button
            type="button"
            className="replay-button"
            title={finished
              ? "The replay reached the end of the stored bars: End the session to see how it went, or Reset to trade again"
              : follows
              ? `Play at ${speedLabel(speed)}`
              : "Open a chart first: the replay plays the bars of the open charts and of what this account trades"}
            aria-label="Play"
            disabled={finished || archived || !follows || busy === "Play"}
            onClick={() => send({ _tag: "Play", speed, follow })}
          >
            <span aria-hidden>▶</span> Play
          </button>
        )}
      <button
        type="button"
        className="replay-button"
        title={finished ? "The replay reached the end of its data: End the session to see how it went, or Reset to trade again" : "Advance one bar"}
        aria-label="Step"
        disabled={finished || archived || busy === "Step"}
        onClick={() => send({ _tag: "Step", bars: 1, follow })}
      >
        <span aria-hidden>⏭</span> Step
      </button>
      <span className="button-group replay-speeds" role="radiogroup" aria-label="Replay speed">
        {REPLAY_SPEEDS.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === speed}
            title={option === "max" ? "As fast as possible" : `${option} bar${option === 1 ? "" : "s"} per second`}
            disabled={archived}
            onClick={() => {
              setSpeed(option)
              if (playing) send({ _tag: "Play", speed: option, follow })
            }}
          >
            {speedLabel(option)}
          </button>
        ))}
      </span>
      <span className="replay-time" title={`Replay ${clock.timeframe} bars; the account's virtual time`}>
        {formatClock(clock.now)}
      </span>
      {!compact && (
        <span className={`replay-status replay-${clock.status.toLowerCase()}`}>
          {finished ? "Replay finished" : playing ? `Playing ${speedLabel(clock.speed)}` : "Paused"}
        </span>
      )}
      {compact && finished && (
        <span className="replay-status replay-finished" title="The replay reached the end of its data: End the session to see how it went">Finished</span>
      )}
      {!compact && (
        <label className="replay-sync" title="Show charts only up to the replay's time, so they never show the future">
          <input type="checkbox" checked={sync} onChange={(event) => syncChartPreference.set(event.target.checked ? "on" : "off")} />
          Sync chart
        </label>
      )}
    </div>
  )
}
